import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { ConfigProvider, Empty, message, Pagination, Table, Tooltip } from 'antd';
import { MakeApiCall } from '../../../../../apis';
import { ConvertParams, defaultPageSize } from '../../../../../config';
import { fakeProImage } from '../../../../../config/fakeData';
import ProductDetailsModal from "../../../../../components/modal";
import { getAmount } from "../../../../../utils/helper";

const AMAZON_BASE_URL = "https://www.amazon.in/dp/";

const Products = ({ userData, selectedMarketplace, selectedCurrencySign, selectedTileData }) => {
    // --- State ---
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState([]);
    const [totals, setTotals] = useState(null);
    const [totalPage, setTotalPage] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [filter, setFilter] = useState({
        page: 1,
        "per-page": defaultPageSize,
        marketplace_id: selectedMarketplace?.id,
        start_date: selectedTileData?.start_date,
        end_date: selectedTileData?.end_date,
        sku: selectedTileData?.sku
    });

    // --- Helpers ---
    const handleResize = useCallback(() => setIsMobile(window.innerWidth < 768), []);

    const openDetails = (record) => {
        setSelectedRow(record);
        setIsModalOpen(true);
    };

    const fetchAnalyticsData = async (currentFilter) => {
        if (!currentFilter?.marketplace_id) {
            setList([]);
            setTotalPage(0);
            return;
        }

        setLoading(true);
        try {
            const [dataRes, totalRes] = await Promise.all([
                MakeApiCall(`dashboard/tiles/get-product-analytics-data${ConvertParams(currentFilter)}`, "get", null, true, {}, userData?.token),
                MakeApiCall(`dashboard/tiles/get-product-analytics-data-total${ConvertParams(currentFilter)}`, "get", null, true, {}, userData?.token)
            ]);

            if (dataRes?.status) {
                setTotalPage(dataRes.data?.pagination?.totalCount || 0);
                setList(dataRes.data?.records?.summaryData || []);
            }
            if (totalRes?.status) setTotals(totalRes.data);
        } catch (error) {
            message.error("Failed to load product data");
        } finally {
            setLoading(false);
        }
    };

    // --- Lifecycle ---
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    useEffect(() => {
        setFilter(prev => ({
            ...prev,
            page: 1,
            marketplace_id: selectedMarketplace?.id,
            start_date: selectedTileData?.start_date,
            end_date: selectedTileData?.end_date,
            sku: selectedTileData?.sku
        }));
    }, [selectedMarketplace, selectedTileData]);

    useEffect(() => {
        fetchAnalyticsData(filter);
    }, [filter, userData?.token]);

    // --- Table Configuration (Desktop Columns with Sorters & Tooltips) ---
    const columns = useMemo(() => {
        return [
            {
                title: "#",
                key: "index",
                width: 60,
                render: (_, __, index) => (filter.page - 1) * filter["per-page"] + index + 1,
            },
            {
                title: "Product Details",
                key: "productDetails",
                fixed: 'left',
                width: 450,
                render: (_, record) => {
                    const productUrl = record?.asin ? `${AMAZON_BASE_URL}${record.asin}` : "#";
                    return (
                        <div className="d-flex align-items-center bdr-left-tbl bg-gray-200 rounded-4 p-3 py-2">
                            <a href={productUrl} target="_blank" rel="noopener noreferrer"
                               className="symbol symbol-65px overflow-hidden me-3 border border-gray-300 rounded-2 shadow-sm">
                                <img src={record?.image_url || fakeProImage} alt="product" style={{ objectFit: 'contain' }} />
                            </a>
                            <div className="d-flex flex-column">
                                <a className="text-gray-800 fs-6 text-hover-danger mb-1 fw-bolder text-one min-w-250px"
                                   href={productUrl} target="_blank" rel="noopener noreferrer">
                                    {record?.product_title || ''}
                                </a>
                                <div className="fs-7 mt-1">
                                    <span><b>SKU:</b> {record?.sku}</span> / <span><b>ASIN:</b> {record?.asin}</span>
                                </div>
                                <div className="fs-7 mt-1">
                                    <span><b>{getAmount(record?.price, selectedCurrencySign)}</b></span> /
                                    <span> {record?.fulfillment_channel}</span> /
                                    <span> COGS {getAmount(record?.item_cogs, selectedCurrencySign)}</span>
                                </div>
                            </div>
                        </div>
                    );
                },
            },
            { title: "Units Sold", dataIndex: "total_units", sorter: (a, b) => a.total_units - b.total_units },
            {
                title: "Refund Units",
                dataIndex: "refund_unit",
                sorter: (a, b) => a.refund_unit - b.refund_unit,
                render: (val, record) => {
                    const breakup = record?.refund_breakup || {};
                    const reasons = breakup.reasons || [];

                    const tooltipContent = (
                        <div className="p-2" style={{ minWidth: '200px' }}>
                            {reasons.length > 0 ? (
                                reasons.map((item, idx) => (
                                    <div key={idx} className="d-flex justify-content-between gap-3 mb-1">
                            <span className="text-capitalize">
                                {item.reason.toLowerCase().replace(/_/g, ' ')}:
                            </span>
                                        <b>{item.count}</b>
                                    </div>
                                ))
                            ) : (
                                <div className="text-muted">No specific reasons provided</div>
                            )}
                        </div>
                    );

                    return (
                        <Tooltip title={tooltipContent} placement="top" color="#1e1e2d">
                <span
                    className="fw-bold cursor-pointer text-danger"
                    style={{ borderBottom: "1px dotted #ff4d4f" }}
                >
                    {val}
                </span>
                        </Tooltip>
                    );
                }
            },
            {
                title: "Refund Cost",
                dataIndex: "refund_cost",
                render: (val) => getAmount(val, selectedCurrencySign),
                sorter: (a, b) => a.refund_cost - b.refund_cost
            },
            {
                title: "Sales",
                dataIndex: "sales",
                render: (val) => <b className="text-gray-800">{getAmount(val, selectedCurrencySign)}</b>,
                sorter: (a, b) => a.sales - b.sales
            },
            { title: "Ad Sales", dataIndex: "ad_sales", render: (val) => getAmount(val, selectedCurrencySign), sorter: (a, b) => a.ad_sales - b.ad_sales },
            { title: "Ad Spend", dataIndex: "ad_spend", render: (val) => getAmount(val, selectedCurrencySign), sorter: (a, b) => a.ad_spend - b.ad_spend },
            { title: "COGS", dataIndex: "cogs", render: (val) => getAmount(val, selectedCurrencySign), sorter: (a, b) => a.cogs - b.cogs },
            { title: "Organic Sales", dataIndex: "organic_sales", render: (val) => getAmount(val, selectedCurrencySign), sorter: (a, b) => a.organic_sales - b.organic_sales },
            {
                title: "Tax",
                // Option 1: Use an array for nested paths (supported by AntD)
                dataIndex: ['more_data', 'tax_liability', 'total'],
                render: (val) => getAmount(val || 0, selectedCurrencySign),
                sorter: (a, b) => {
                    const taxA = a?.more_data?.tax_liability?.total || 0;
                    const taxB = b?.more_data?.tax_liability?.total || 0;
                    return taxA - taxB;
                }
            },
            { title: "Fees", dataIndex: "fees", render: (val) => getAmount(val, selectedCurrencySign), sorter: (a, b) => a.fees - b.fees },
            { title: "Gross Profit", dataIndex: "gross_profit", render: (val) => getAmount(val, selectedCurrencySign), sorter: (a, b) => a.gross_profit - b.gross_profit },
            {
                title: "Net Profit",
                dataIndex: "net_profit",
                render: (val) => <span className="fw-bold text-success">{getAmount(val, selectedCurrencySign)}</span>,
                sorter: (a, b) => a.net_profit - b.net_profit
            },
            {
                title: "Inventory",
                dataIndex: "inventory",
                render: (val, record) => {
                    const inv = record?.more_data?.total_inventories || {};
                    const tooltipContent = (
                        <div className="p-2" style={{ minWidth: '180px' }}>
                            <div>FBA Available: <b>{inv.fba_available_inventory ?? 0}</b></div>
                            <div>Reserved: <b>{inv.reserved_inventory ?? 0}</b></div>
                            <div>Inbound: <b>{inv.inbound_inventory ?? 0}</b></div>
                        </div>
                    );
                    return (
                        <Tooltip title={tooltipContent} placement="top" color="#1e1e2d">
                            <span className="fw-bold cursor-pointer" style={{ borderBottom: "1px dotted #adadad" }}>{val}</span>
                        </Tooltip>
                    );
                },
                sorter: (a, b) => a.inventory - b.inventory
            },
            { title: "Days Until Stockout", dataIndex: "days_on_hand", key: "days_on_hand", sorter: (a, b) => a.days_on_hand - b.days_on_hand },
            {
                title: "Inventory Health",
                dataIndex: "inventory_status",
                key: "inventory_status",
                render: (status) => {
                    let badgeClass = "badge-light-primary";
                    const lowerStatus = status?.toLowerCase() || "";
                    if (lowerStatus.includes("in stock") || lowerStatus === "healthy") badgeClass = "badge-light-success";
                    else if (lowerStatus.includes("low") || lowerStatus.includes("alert")) badgeClass = "badge-light-warning";
                    else if (lowerStatus.includes("out") || lowerStatus.includes("overstock")) badgeClass = "badge-light-danger";
                    return <span className={`badge ${badgeClass} fw-bold px-4 py-3`}>{status || 'N/A'}</span>;
                },
                sorter: (a, b) => (a.inventory_status || "").localeCompare(b.inventory_status || "")
            },
            {
                title: "Status",
                dataIndex: "status",
                render: (status) => (
                    <span className={`badge ${status === 0 ? 'badge-light-success' : 'badge-light-danger'}`}>
                        {status === 0 ? 'Active' : 'Inactive'}
                    </span>
                )
            },
            {
                title: "Details",
                key: "details",
                render: (_, record) => (
                    <span className="cursor-pointer fw-bold text-hover-danger" style={{ color: '#D8372E' }}
                          onClick={() => openDetails(record)}>
                        See Details
                    </span>
                )
            },
        ];
    }, [filter, selectedCurrencySign]);

    // --- Mobile Card Render (6-Field Info) ---
    const renderMobileCards = () => (
        <div className="d-flex flex-column gap-3">
            {list.map((item, index) => (
                <div
                    key={item.sku || index}
                    className="card border-0 shadow-sm p-4 rounded-4 cursor-pointer"
                    style={{ background: '#fff', border: '1px solid #f0f0f0' }}
                    onClick={() => openDetails(item)}
                >
                    <div className="d-flex align-items-center mb-4">
                        <img src={item.image_url || fakeProImage} alt="pro" className="rounded-2 me-3" style={{ width: '50px', height: '50px', objectFit: 'contain', border: '1px solid #f5f5f5' }} />
                        <div className="overflow-hidden">
                            <div className="fw-bolder text-gray-800 text-truncate fs-6" style={{ maxWidth: '220px' }}>{item.product_title || 'N/A'}</div>
                            <div className="text-muted fs-8">
                                <b>SKU:</b> {item.sku} <span className="mx-1">|</span> <b>ASIN:</b> {item.asin}
                            </div>
                        </div>
                    </div>

                    <div className="row g-4 border-top pt-4">
                        <div className="col-4">
                            <div className="fs-9 text-uppercase text-muted fw-bold mb-1">Units</div>
                            <div className="fw-bolder text-dark">{item.total_units}</div>
                        </div>
                        <div className="col-4">
                            <div className="fs-9 text-uppercase text-muted fw-bold mb-1">Sales</div>
                            <div className="fw-bolder text-dark">{getAmount(item.sales, selectedCurrencySign)}</div>
                        </div>
                        <div className="col-4">
                            <div className="fs-9 text-uppercase text-muted fw-bold mb-1">Net Profit</div>
                            <div className={`fw-bolder ${item.net_profit >= 0 ? 'text-success' : 'text-danger'}`}>
                                {getAmount(item.net_profit, selectedCurrencySign)}
                            </div>
                        </div>
                        <div className="col-4">
                            <div className="fs-9 text-uppercase text-muted fw-bold mb-1">Refunds</div>
                            {/* Wrap the refund unit in a Tooltip for mobile tap-to-view */}
                            <Tooltip
                                trigger="click" // Better for mobile UX to ensure it opens on tap
                                title={
                                    <div className="p-2" style={{minWidth: '180px'}}>
                                        <div className="fw-bold mb-2 border-bottom pb-1" style={{color: '#ff4d4f'}}>
                                            Refund Reasons
                                        </div>
                                        {(item?.refund_breakup?.reasons || []).length > 0 ? (
                                            item.refund_breakup.reasons.map((reasonObj, idx) => (
                                                <div key={idx}
                                                     className="d-flex justify-content-between gap-3 mb-1 fs-8">
                            <span className="text-capitalize">
                                {reasonObj.reason.toLowerCase().replace(/_/g, ' ')}:
                            </span>
                                                    <b>{reasonObj.count}</b>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="fs-8 text-muted">No reasons provided</div>
                                        )}
                                    </div>
                                }
                                color="#1e1e2d"
                            >
                                <div
                                    className="fw-bolder text-danger"
                                    style={{borderBottom: "1px dotted #ff4d4f", display: 'inline-block'}}
                                >
                                    {item.refund_unit}
                                </div>
                            </Tooltip>
                        </div>
                        <div className="col-4">
                            <div className="fs-9 text-uppercase text-muted fw-bold mb-1">Ref. Cost</div>
                            <div
                                className="fw-bolder text-muted">{getAmount(item.refund_cost, selectedCurrencySign)}</div>
                        </div>
                        <div className="col-4">
                            <div className="fs-9 text-uppercase text-muted fw-bold mb-1">Ad Spend</div>
                            <div
                                className="fw-bolder text-primary">{getAmount(item.ad_spend, selectedCurrencySign)}</div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="products-container">
            <div className="row gy-5 g-xl-5">
                <div className="col-xxl-12">
                    {isMobile ? (
                        <div className="mobile-view-container px-1">
                            {list.length > 0 ? renderMobileCards() : <Empty description="No Products Found"/>}
                        </div>
                    ) : (
                        <div className="table-responsive shadow-sm rounded">
                            <Table
                                scroll={{x: "max-content"}}
                                columns={columns}
                                loading={loading}
                                dataSource={list}
                                rowKey={(record) => record.sku || record.asin}
                                pagination={false}
                                // ... inside your Table component
                                summary={() => totals && (
                                    <Table.Summary fixed>
                                        <Table.Summary.Row className="bg-light fw-bolder">
                                            {/* 1. This cell aligns with the "#" column (Index 0) */}
                                            <Table.Summary.Cell index={0} className="bg-light text-center">

                                            </Table.Summary.Cell>

                                            {/* 2. This cell aligns with "Product Details" (Index 1) and is FIXED LEFT */}
                                            <Table.Summary.Cell index={1} className="bg-light fs-6" fixed="left">
                                                TOTALS
                                            </Table.Summary.Cell>

                                            {/* 3. The rest of the data cells (Index 2 onwards) */}
                                            <Table.Summary.Cell index={2}>{totals.total_units}</Table.Summary.Cell>
                                            <Table.Summary.Cell index={3}>{totals.refund_unit}</Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}>{getAmount(totals.refund_cost, selectedCurrencySign)}</Table.Summary.Cell>
                                            <Table.Summary.Cell index={5}>
                                                <span className="text-dark">{getAmount(totals.sales, selectedCurrencySign)}</span>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={6}>{getAmount(totals.ad_sales, selectedCurrencySign)}</Table.Summary.Cell>
                                            <Table.Summary.Cell index={7}>{getAmount(totals.ad_spend, selectedCurrencySign)}</Table.Summary.Cell>
                                            <Table.Summary.Cell index={8}>{totals.cogs?.toFixed(2)}</Table.Summary.Cell>
                                            <Table.Summary.Cell index={9}>{getAmount(totals.organic_sales, selectedCurrencySign)}</Table.Summary.Cell>
                                            <Table.Summary.Cell index={10}>{getAmount(totals.tax, selectedCurrencySign)}</Table.Summary.Cell>
                                            <Table.Summary.Cell index={11}>{getAmount(totals.fees, selectedCurrencySign)}</Table.Summary.Cell>
                                            <Table.Summary.Cell index={12}>{getAmount(totals.gross_profit, selectedCurrencySign)}</Table.Summary.Cell>
                                            <Table.Summary.Cell index={13}>
                                                <span className={totals.net_profit >= 0 ? 'text-success' : 'text-danger'}>
                                                    {getAmount(totals.net_profit, selectedCurrencySign)}
                                                </span>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={14}>{totals.inventory}</Table.Summary.Cell>
                                            <Table.Summary.Cell index={15} colSpan={2}>-</Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                )}
                                locale={{ emptyText: <Empty description="No Products Found" /> }}
                                components={{
                                    header: {
                                        row: (props) => <tr {...props} className="fw-bolder text-dark bg-cus" />,
                                    },
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="card border-0 shadow-sm mt-5">
                <div className="card-body py-4">
                    <Pagination
                        current={filter.page}
                        pageSize={filter['per-page']}
                        total={totalPage}
                        showSizeChanger
                        onChange={(page, pageSize) => setFilter(prev => ({ ...prev, page, "per-page": pageSize }))}
                        showTotal={(total, range) => isMobile ? `Total: ${total}` : `${range[0]}-${range[1]} of ${total} products`}
                        responsive
                        size={isMobile ? "small" : "default"}
                        simple={isMobile}
                    />
                </div>
            </div>

            <ProductDetailsModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                data={selectedRow}
                currencySign={selectedCurrencySign}
            />
        </div>
    );
};

export default React.memo(Products);